import { useEffect, useState } from 'react';

import { useApi } from '@backstage/core-plugin-api';
import {
  NotificationApi,
  notificationApiRef,
} from '../../../../../apis/notificationApi';
import { iqeAgentApiRef } from '../../../../../apis/iqeAgentApi';

import { toIqeProjectsDomain } from '../dto/responseDto/iqeProjects.response.dto';
import type {
  IqeProject,
  IqeProjectStatus,
  IqeProjectAction,
  EnhancementTestPlanSelection,
} from '../interfaces/ui';
import {
  mapFormToSaveProjectRequest,
  mapIqeProjectToActionRequest,
} from '../dto/requestDto/saveProject.request.dto';
import { mapSaveProjectResponse } from '../dto/responseDto/saveProject.response.dto';
import {
  isStopRunAccepted,
  toStopRunStatusUpdate,
} from '../dto/responseDto/stopRun.response.dto';

import { toUiProjectStatus } from '../dto/sharedDto/status.dto';

import type { ProjectFormData } from '../interfaces/ui/addProjectModal';
import { IqeProjectOperationRequest } from '../interfaces/api/requests/saveProject.request';

export const useProjectsState = (apmNumber: string) => {
  const notificationApi: NotificationApi = useApi(notificationApiRef);
  const iqeAgentApi = useApi(iqeAgentApiRef);

  const [projects, setProjects] = useState<IqeProject[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [refreshingProjectIds, setRefreshingProjectIds] = useState<string[]>(
    [],
  );
  const [rerunningProjectIds, setRerunningProjectIds] = useState<string[]>([]);
  const [isPerformingAction, setIsPerformingAction] = useState<boolean>(false);
  const [isStoppingRun, setIsStoppingRun] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!apmNumber) {
      setProjects([]);
      return;
    }

    fetchIqeProjects(apmNumber);
  }, [apmNumber]);

  const sortProjectsByCreatedDate = (list: IqeProject[]): IqeProject[] => {
    return [...list].sort(
      (a, b) =>
        (b.createdAt instanceof Date ? b.createdAt.getTime() : 0) -
        (a.createdAt instanceof Date ? a.createdAt.getTime() : 0),
    );
  };

  const fetchIqeProjects = async (apmNumber: string) => {
    setLoading(true);
    try {
      const response = await iqeAgentApi.getIqeProjects(apmNumber);

      if (!response || response?.status?.toLowerCase() !== 'success') {
        throw new Error(
          `Failed to fetch projects for APM ${apmNumber}: ${
            response?.message || 'Unknown error'
          }`,
        );
      }

      const projectsData: IqeProject[] = toIqeProjectsDomain(response);

      if (projectsData.length === 0) {
        setError(`No projects found for APM ${apmNumber}`);
        setProjects([]);
        return;
      }

      setProjects(sortProjectsByCreatedDate(projectsData));
    } catch (error) {
      setProjects([]);
      setError(`Error occurred - ${error}`);
      notificationApi.sendNotification({
        message: `Error occurred - ${error}`,
        disapperAfterMs: 4000,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  async function saveProject(formData: ProjectFormData, apmNumber: string) {
    try {
      const saveApiRequestPayload: IqeProjectOperationRequest =
        mapFormToSaveProjectRequest(formData);

      setSaving(true);

      const response = await iqeAgentApi.performProjectOperation(
        apmNumber,
        saveApiRequestPayload,
      );

      if (
        response?.status?.toLowerCase() !== 'success' ||
        !response?.data?.projectId
      ) {
        throw new Error(response?.data?.message || 'Failed to save project');
      }

      // Refresh the projects list after saving
      const newIqeProject: IqeProject = mapSaveProjectResponse(
        formData,
        response.data,
      );

      setProjects(prevProjects =>
        sortProjectsByCreatedDate([...prevProjects, newIqeProject]),
      );

      notificationApi.sendNotification({
        message: `${formData.projectName} submitted successfully`,
        disapperAfterMs: 4000,
        severity: 'info',
      });
    } catch (error) {
      setError(`Error occurred - ${error}`);
      notificationApi.sendNotification({
        message: `Error occurred - ${error}`,
        disapperAfterMs: 4000,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  }

  const updateProjectStatus = (statusData: any) => {
    if (!statusData) return;

    const rawStatus = statusData?.currentStatus || statusData?.status;
    const newStatus: IqeProjectStatus = toUiProjectStatus(rawStatus);

    setProjects(prevProjects =>
      prevProjects.map(p =>
        p.id === statusData?.projectId
          ? {
              ...p,
              status: newStatus,
              transactionId: statusData?.transactionId || p.transactionId,
            }
          : p,
      ),
    );
  };

  const rerunProjectValidation = async (iqeProjectId: string) => {
    setRerunningProjectIds(prevProjectIds => {
      if (prevProjectIds.includes(iqeProjectId)) {
        return prevProjectIds;
      }

      return [...prevProjectIds, iqeProjectId];
    });

    try {
      const project = getProjectById(iqeProjectId);
      if (!project)
        throw new Error(`Project ${iqeProjectId} not found in state`);

      const payload = mapIqeProjectToActionRequest(project, 'CREATE_RERUN');
      const response = await iqeAgentApi.performProjectOperation(
        apmNumber,
        payload,
      );

      if (!response || response?.status?.toLowerCase() !== 'success') {
        throw new Error(`Failed to rerun validation for project`);
      }

      updateProjectStatus(response?.data);
      notificationApi.sendNotification({
        message:
          response.message || 'Project validation rerun triggered successfully',
        disapperAfterMs: 4000,
        severity: 'info',
      });
    } catch (error) {
      setError(`Error occurred - ${error}`);
      notificationApi.sendNotification({
        message: `Error occurred - ${error}`,
        disapperAfterMs: 4000,
        severity: 'error',
      });
    } finally {
      setRerunningProjectIds(prevProjectIds =>
        prevProjectIds.filter(projectId => projectId !== iqeProjectId),
      );
    }
  };
  const isProjectActionOperation = (
    operation: IqeProjectAction,
  ): operation is Exclude<IqeProjectAction, 'CREATE'> => {
    return operation !== 'CREATE';
  };

  const performProjectOperation = async (
    apmNumber: string,
    iqeProjectId: string,
    operation: IqeProjectAction,
    userPrompt = '',
    enhancementSelection?: EnhancementTestPlanSelection,
  ): Promise<string | null> => {
    setIsPerformingAction(true);
    try {
      if (!isProjectActionOperation(operation)) {
        throw new Error('CREATE is not supported in perform action flow');
      }

      const project = getProjectById(iqeProjectId);
      if (!project) {
        throw new Error('Project ' + iqeProjectId + ' not found in state');
      }

      const payload = mapIqeProjectToActionRequest(
        project,
        operation,
        userPrompt,
        enhancementSelection,
      );

      const response = await iqeAgentApi.performProjectOperation(
        apmNumber,
        payload,
      );

      if (!response || response?.status?.toLowerCase() !== 'success') {
        throw new Error('Failed to perform ' + operation + ' for project');
      }

      updateProjectStatus(response?.data);

      notificationApi.sendNotification({
        message:
          response.message || `${operation.toUpperCase()} request submitted.`,
        disapperAfterMs: 4000,
        severity: 'info',
      });

      return response.data?.transactionId || null;
    } catch (error) {
      setError('Error occurred - ' + error);
      notificationApi.sendNotification({
        message: 'Error occurred - ' + error,
        disapperAfterMs: 4000,
        severity: 'error',
      });
    } finally {
      setIsPerformingAction(false);
    }

    return null;
  };

  function deleteProject(
    projectId: string,
    projects: IqeProject[],
  ): IqeProject[] {
    return projects.filter(p => p.id !== projectId);
  }

  const getProjectById = (projectId: string): IqeProject | undefined => {
    return projects.find(p => p.id === projectId);
  };

  async function stopRun(
    apmNumber: string,
    iqeProjectId: string,
    runId: string,
  ): Promise<boolean> {
    setIsStoppingRun(true);
    try {
      const response = await iqeAgentApi.stopRun(apmNumber, {
        projectId: iqeProjectId,
        transactionId: runId,
      });

      if (!isStopRunAccepted(response)) {
        throw new Error(response?.message || 'Stop Run request failed');
      }

      const statusUpdate = toStopRunStatusUpdate(response, iqeProjectId);
      if (statusUpdate.status) {
        updateProjectStatus(statusUpdate);
      }

      notificationApi.sendNotification({
        message: 'Run stopped successfully.',
        disapperAfterMs: 4000,
        severity: 'success',
      });
      return true;
    } catch {
      notificationApi.sendNotification({
        message: "Couldn't stop the run. Please try again.",
        disapperAfterMs: 4000,
        severity: 'error',
      });
      return false;
    } finally {
      setIsStoppingRun(false);
    }
  }

  async function refreshProjectStatus(apmNumber: string, iqeProjectId: string) {
    setRefreshingProjectIds(prevProjectIds => {
      if (prevProjectIds.includes(iqeProjectId)) {
        return prevProjectIds;
      }

      return [...prevProjectIds, iqeProjectId];
    });

    try {
      const response = await iqeAgentApi.getIqeProjectStatus(
        apmNumber,
        iqeProjectId,
      );
      if (!response || response?.status?.toLowerCase() !== 'success') {
        throw new Error(
          `Failed to fetch project status for IQE project ${iqeProjectId}: ${
            response?.message || 'Unknown error'
          }`,
        );
      }

      updateProjectStatus(response?.data);
    } catch (error) {
      notificationApi.sendNotification({
        message: `Error occurred - ${error}`,
        disapperAfterMs: 4000,
        severity: 'error',
      });
    } finally {
      setRefreshingProjectIds(prevProjectIds =>
        prevProjectIds.filter(projectId => projectId !== iqeProjectId),
      );
    }
  }

  return {
    projects,
    iqeProjectsError: error,
    loadingIqeProjects: loading,
    saveProject,
    isSavingProject: isSaving,
    // updateProjectsAfterExecution,
    deleteProject,
    getProjectById,
    refreshProjectStatus,
    refreshingProjectIds,
    rerunProjectValidation,
    rerunningProjectIds,
    isPerformingAction,
    performProjectOperation,
    isStoppingRun,
    stopRun,
  };
};
